import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Calendar,
  BookOpen,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  Mail,
  UsersRound,
  UserCog,
  FileText,
  CalendarDays,
  ImagePlay,
  BookMarked,
  Package,
  CreditCard,
  Tag,
  GraduationCap,
  UserPlus
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "/logo.png";

const navItems = [
  { name: 'Dashboard',  path: '/admin/dashboard',  icon: LayoutDashboard },
  { name: 'Leads',      path: '/admin/leads',      icon: UserPlus },
  { name: 'Contacts',   path: '/admin/contacts',   icon: Mail, aliases: ['/admin/messages'] },
  { name: 'Messages',   path: '/admin/chat',       icon: MessageSquare, testId: 'admin-nav-chat' },
  { name: 'Students',   path: '/admin/students',   icon: Users },
  { name: 'Calendar',   path: '/admin/calendar',   icon: Calendar, aliases: ['/admin/classes'], testId: 'admin-nav-classes' },
  { name: 'Team',       path: '/admin/teachers',   icon: UserCog,        adminOnly: true },
  { name: 'Groups',     path: '/admin/groups',     icon: UsersRound },
  { name: 'Activities', path: '/admin/activities', icon: BookOpen },
  { name: 'Plans',      path: '/admin/plans',      icon: Package,        adminOnly: true },
  { name: 'Billing',    path: '/admin/billing',    icon: CreditCard,     adminOnly: true },
  { name: 'Coupons',    path: '/admin/coupons',    icon: Tag,            adminOnly: true },
  { name: 'Availability', path: '/admin/availability', icon: CalendarDays, adminOnly: true },
  { name: 'Level Test',     path: '/admin/level-test',    icon: GraduationCap, adminOnly: true },
  { name: 'Testimonials',   path: '/admin/testimonials',  icon: MessageSquare, adminOnly: true },
  { name: 'Site Content',  path: '/admin/site-content',  icon: FileText,    adminOnly: true, aliases: ['/admin/cms'], testId: 'admin-nav-content' },
  { name: 'Media',      path: '/admin/media',      icon: ImagePlay,      adminOnly: true },
  { name: 'Documentation', path: '/admin/documentation', icon: BookMarked },
  { name: 'Settings',      path: '/admin/settings',      icon: Settings,    adminOnly: true },
];

export const AdminLayout = ({ children }) => {
  const { settings } = usePublicSettings();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const { user, logout, isStrictAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const siteName = settings.site_name || "Frankly Talking English";
  const userMenuRef = useRef(null);
  const visibleNavItems = navItems.filter(item => !item.adminOnly || isStrictAdmin);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  // Fetch notification counts every 30s
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/notifications/counts`);
        setNotifCount(res.data.total || 0);
      } catch (_) {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  return (
    <div className="min-h-screen bg-[#F8F9FC] flex">
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-[#1A2E6E] text-white flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex-shrink-0 flex items-center justify-between px-6 border-b border-white/10">
          <Link to="/admin/dashboard" className="flex items-center">
            <img
              src={LOGO_URL}
              alt={siteName}
              className="h-10 w-auto brightness-0 invert"
            />
          </Link>
          <button
            className="lg:hidden text-white/70 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1 scrollbar-thin scrollbar-thumb-white/20">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const activePaths = [item.path, ...(item.aliases || [])];
            const isActive = activePaths.some(path => (
              location.pathname === path || location.pathname.startsWith(`${path}/`)
            ));
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId || `admin-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`
                  flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200
                  ${isActive
                    ? 'bg-white/15 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                <Icon size={18} className="flex-shrink-0" />
                <span className="font-medium text-sm truncate">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Footer link — always visible at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-white/10">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-2.5 text-white/60 hover:text-white transition-colors text-sm"
          >
            Back to Website
          </Link>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-[#E8EAF0] flex items-center justify-between px-4 lg:px-8">
          <button
            className="lg:hidden p-2 text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg"
            onClick={() => setSidebarOpen(true)}
            data-testid="admin-mobile-menu-btn"
          >
            <Menu size={24} />
          </button>

          <h1 className="hidden lg:block text-lg font-semibold text-[#1A2E6E]">
            Admin Portal
          </h1>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/chat')}
              className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg relative"
              aria-label="Notifications"
            >
              <Bell size={20} />
              {notifCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-[#D72B2B] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {notifCount > 9 ? '9+' : notifCount}
                </span>
              )}
            </button>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 hover:bg-[#1A2E6E]/5 rounded-lg transition-colors"
                data-testid="admin-user-menu-btn"
              >
                <div className="w-8 h-8 rounded-full bg-[#D72B2B] flex items-center justify-center text-white font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </div>
                <span className="hidden sm:block text-sm font-medium text-[#1A2E6E]">
                  {user?.name || 'Admin'}
                </span>
                <ChevronDown size={16} className="text-[#1A2E6E]/60" />
              </button>

              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-[#E8EAF0] py-2 z-50"
                >
                  <div className="px-4 py-2 border-b border-[#E8EAF0]">
                    <p className="text-sm font-medium text-[#1A2E6E]">{user?.name}</p>
                    <p className="text-xs text-[#1A2E6E]/60">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    data-testid="admin-logout-btn"
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
